import React from 'react';
import { Container, Table } from 'reactstrap';
import { LicenseVersion } from '../../../core/domainModels/licences/LicenseVersion';
import { LicenseTachoModule } from '../../../core/domainModels/modules/LicenseTachoModule';
import { dateToStringFormat } from '../../../core/helpers/moment';
import { useIntl } from 'react-intl';
import { TachoModal } from '../../components/TachoModal';
import { TachoEditModalTitle } from '../../components/TachoModal/TachoEditTitle';
import { Alignment, Button, Icon, JustifyContents } from 'tacho-ui';
import { maxInt } from './services/licenceService';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../state/ApplicationState';

interface ShowVersionsModalProps {
    licenseVersions: Array<LicenseVersion>;
    isOpen: boolean;
    onToggle: () => void;
    onEditVersion: (version: LicenseVersion) => void;
}

export const ShowVersionsModal = (props: ShowVersionsModalProps) => {
    const { formatMessage } = useIntl();
    const distributors = useSelector((state: ApplicationState) => state.distributors.distributors);
    const formatAllowedModules = (modules: Array<LicenseTachoModule>) => {
        return modules
            .map((item: LicenseTachoModule) => `${formatMessage({ id: item.tachoModuleId })}: ${item.maximumLoggedUsers}`)
            .join(', ');
    };
    const sortedVersions = [...props.licenseVersions].sort((a, b) => a.validFrom.diff(b.validFrom).milliseconds);

    return (
        <TachoModal
            height="auto"
            size="xl"
            isOpen={props.isOpen}
            title={<TachoEditModalTitle icon={Icon.Licenses} titleToTranslation="versionsPage" />}
            hideSubmitButton
            cancelLabelToTranslate="tachoModalClose"
            onToggle={() => props.onToggle()}
        >
            <Container className="licencesVersionDetailsContainer d-flex flex-column px-4 mt-2">
                <Table className="licenceTableContainer my-1" bordered>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{formatMessage({ id: 'licencesValidFrom' })}</th>
                            <th>{formatMessage({ id: 'licencesValidTo' })}</th>
                            <th>{formatMessage({ id: 'licencesPaymentDeadline' })}</th>
                            <th>{formatMessage({ id: 'licencesNumberOfCars' })}</th>
                            <th>{formatMessage({ id: 'licencesPaymentStatus' })}</th>
                            <th>{formatMessage({ id: 'licencesLicenseType' })}</th>
                            <th>{formatMessage({ id: 'licencesDistributor' })}</th>
                            <th>{formatMessage({ id: 'licencesAllowedModules' })}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedVersions.map((version, index) => (
                            <tr key={version.id}>
                                <td>{index + 1}</td>
                                <td>{dateToStringFormat(version.validFrom)}</td>
                                <td>{dateToStringFormat(version.validTo)}</td>
                                <td>{dateToStringFormat(version.paymentDeadline)}</td>
                                <td>
                                    {version.maximumNumberOfSupportedCars === maxInt
                                        ? formatMessage({ id: 'licenceCarsFull' })
                                        : version.maximumNumberOfSupportedCars}
                                </td>
                                <td>{formatMessage({ id: version.paymentStatus })}</td>
                                <td>{formatMessage({ id: version.type })}</td>
                                <td>{distributors.find(dist => version.distributorId === dist.id)?.name}</td>
                                <td>{formatAllowedModules(version.allowedModules)}</td>
                                <td>
                                    <Button
                                        justify={JustifyContents.center}
                                        align={Alignment.center}
                                        gap={4}
                                        icon={Icon.Edit}
                                        className="secondary"
                                        onClick={() => props.onEditVersion(version)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Container>
        </TachoModal>
    );
};
